import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Button, FilterIcon, Input, SearchIcon, DensityMediumIcon as ColumnsIcon, useStatefulRef } from '@forge/common';
import ActivityFilterModal, { IFilterSelections } from '../activityFilter/ActivityFilterModal';
import ActivityColumnPanel from './ActivityColumnPanel';
import { ACTIVITY_SEARCH_DEBOUNCE_MS, isValidUserId } from '../../utils';
import { useDebouncedValue } from '../shared/useDebouncedValue';
import { IColumnConfig } from '../../Redux/activityModels';
import { ActivityAppDispatch, useActivitySelector } from '../../Redux/activityStore';
import { fetchFilterOptions } from '../../Redux/activitySlice';
import {
  selectFilterOptions,
  selectFilterOptionsLoading,
  selectFilterOptionsError,
  selectActivityFilters,
  selectUserDisplayNames,
} from '../../Redux/activitySelectors';

export interface IActivityToolbarProps {
  onSearchChange: (searchText: string) => void;
  customerIds: string[];
  siOrgId?: string;
  siteIds?: string[];
  onFilterApply: (selections: IFilterSelections) => void;
  columnConfig: IColumnConfig;
  onColumnApply: (config: IColumnConfig) => void;
}

const ActivityToolbar: React.FC<IActivityToolbarProps> = ({
  onSearchChange,
  customerIds,
  siOrgId,
  siteIds,
  onFilterApply,
  columnConfig,
  onColumnApply,
}) => {
  const dispatch = useDispatch<ActivityAppDispatch>();
  const { t } = useTranslation();
  const filterOptions = useActivitySelector(selectFilterOptions);
  const filterOptionsLoading = useActivitySelector(selectFilterOptionsLoading);
  const filterOptionsError = useActivitySelector(selectFilterOptionsError);
  const { statuses: selectedStatuses, activity: selectedActivity, siteIds: selectedSiteIds, userIds: selectedUserIds } = useActivitySelector(selectActivityFilters);
  const userDisplayNames = useActivitySelector(selectUserDisplayNames);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [hasSearchText, setHasSearchText] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isColumnOpen, setIsColumnOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const columnBtnRef = useStatefulRef<HTMLButtonElement>();
  const inputRef = useRef<HTMLInputElement>(null);
  const didMountRef = useRef(false);
  const debouncedSearchText = useDebouncedValue(searchText, ACTIVITY_SEARCH_DEBOUNCE_MS);

  const isFilterActive =
    selectedStatuses.length > 0 || selectedActivity.length > 0 || selectedSiteIds.length > 0 || selectedUserIds.length > 0;

  const userOptions = useMemo(() => {
    const ids = filterOptions?.createdByIds ?? [];
    // Drop entries with no resolved display name instead of falling back to the raw id.
    return ids
      .filter(isValidUserId)
      .map(id => ({ id, label: (userDisplayNames[id] || '').trim() }))
      .filter(u => u.label.length > 0);
  }, [filterOptions?.createdByIds, userDisplayNames]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        isSearchOpen &&
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isSearchOpen]);

  useEffect(() => {
    if (isSearchOpen) {
      inputRef.current?.focus();
    }
  }, [isSearchOpen]);

  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    onSearchChange(debouncedSearchText);
  }, [debouncedSearchText, onSearchChange]);

  const handleSearchIconClick = useCallback(() => {
    setIsSearchOpen(prev => !prev);
  }, []);

  const handleFilterIconClick = useCallback(() => {
    // Always fetch fresh filter options when the panel opens (even if we already
    // have cached options) so that selections stay in sync with the latest data.
    // The request is skipped only if another fetch is already in flight.
    if (customerIds.length > 0 && !filterOptionsLoading) {
      dispatch(fetchFilterOptions({ customerIds, siOrgId, ...(siteIds?.length ? { siteIds } : {}) }));
    }
    setIsFilterOpen(true);
  }, [customerIds, filterOptionsLoading, dispatch, siOrgId, siteIds]);

  const handleFilterClose = useCallback(() => {
    setIsFilterOpen(false);
  }, []);

  const handleFilterRefresh = useCallback(() => {
    dispatch(fetchFilterOptions({ customerIds, siOrgId, ...(siteIds?.length ? { siteIds } : {}) }));
  }, [customerIds, dispatch, siOrgId, siteIds]);

  const handleFilterApply = useCallback(
    (selections: IFilterSelections) => {
      onFilterApply(selections);
      setIsFilterOpen(false);
    },
    [onFilterApply],
  );

  const handleColumnIconClick = useCallback(() => {
    setIsColumnOpen(prev => !prev);
  }, []);

  const handleColumnApply = useCallback(
    (config: IColumnConfig) => {
      onColumnApply(config);
      setIsColumnOpen(false);
    },
    [onColumnApply],
  );

  const handleColumnCancel = useCallback(() => {
    setIsColumnOpen(false);
  }, []);

  const handleInput = useCallback(
    (e: React.FormEvent<HTMLInputElement>) => {
      const value = (e.target as HTMLInputElement).value;
      setHasSearchText(value.length > 0);
      setSearchText(value);
    },
    [],
  );

  return (
    <div ref={containerRef} className="tw-flex tw-items-center tw-justify-end tw-gap-2">
      <div data-testid="search-wrapper" className={isSearchOpen ? undefined : 'tw-hidden'}>
        <Input
          ref={inputRef}
          id="activity-log-search"
          placeholder={t('activityLogSearchPlaceholder')}
          onInput={handleInput}
        />
      </div>
      <div className="tw-relative">
        <Button
          variant="quiet-primary"
          icon={<SearchIcon />}
          aria-label={t('activityLogSearchPlaceholder')}
          aria-expanded={isSearchOpen}
          onClick={handleSearchIconClick}
        />
        {hasSearchText && (
          <span className="tw-absolute tw-top-1 tw-right-1 tw-w-2 tw-h-2 tw-rounded-full tw-bg-red-500 tw-pointer-events-none" />
        )}
      </div>
      <div className="tw-relative">
        <Button
          variant="quiet-primary"
          icon={<FilterIcon />}
          aria-label={t('activityLogFilterTitle')}
          aria-expanded={isFilterOpen}
          onClick={handleFilterIconClick}
        />
        {isFilterActive && (
          <span className="tw-absolute tw-top-1 tw-right-1 tw-w-2 tw-h-2 tw-rounded-full tw-bg-red-500 tw-pointer-events-none" />
        )}
      </div>
      <div className="tw-relative">
        <Button
          ref={columnBtnRef}
          variant="quiet-primary"
          icon={<ColumnsIcon />}
          aria-label={t('activityLogCustomizeColumns')}
          aria-expanded={isColumnOpen}
          onClick={handleColumnIconClick}
        />
      </div>
      {isFilterOpen && (
        <ActivityFilterModal
          filterOptions={filterOptions}
          isLoading={filterOptionsLoading}
          error={filterOptionsError}
          selectedStatuses={selectedStatuses}
          selectedActivity={selectedActivity}
          selectedSiteIds={selectedSiteIds}
          selectedUserIds={selectedUserIds}
          userOptions={userOptions}
          showSiteFilter={!siteIds?.length}
          onClose={handleFilterClose}
          onApply={handleFilterApply}
          onRefresh={handleFilterRefresh}
        />
      )}
      {isColumnOpen && (
        <ActivityColumnPanel
          columnConfig={columnConfig}
          anchorRef={columnBtnRef}
          onApply={handleColumnApply}
          onCancel={handleColumnCancel}
        />
      )}
    </div>
  );
};

export default ActivityToolbar;

